import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { apiModelTypeToAutomationStepType } from '../../../extViewmodels/sdk/Utils';
import { AutomationStepAction, EditAutomationTemplateStepsContext } from '../../../models/Automations';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { useUpdateAutomationStepMutation } from '../../../queries/Automation/mutations/steps';
import { useAutomationTemplateIdStepOrderUpdateMutation } from '../../../queries/AutomationTemplate/useAutomationTemplateIdStepOrderUpdateMutation';
import { AutomationStepCard } from '../../components/automation/AutomationStepCard';
import { EditAutomationTemplateItemBumpers } from '../../components/automation/EditAutomationTemplateItemBumpers';
import { EditAutomationTemplateItemSeparator } from '../../components/automation/EditAutomationTemplateItemSeparator';
import { EditAutomationTemplateSegment } from '../../components/automation/EditAutomationTemplateSegment';
import { AutomationTemplateStepIndicatorIcon } from '../../components/svgs/icons/AutomationTemplateStepIndicator';
import { useAutomationEditorContext } from './hooks';
import { styleSheet } from './styles';

interface IEditAutomationStepProps {
	automationTemplateId: string;
	index: number;
	onStepMenuItemClicked?(step: Api.IAutomationStep, action: AutomationStepAction): void;
	scrollToAnchorRef?: React.MutableRefObject<HTMLElement>;
	step: Api.IAutomationStep;
	stepIndicatorIconRef?: React.Ref<HTMLDivElement>;
	onStepChanged?: () => void;
	impersonationContext: Api.IImpersonationContext;
}

interface IEditAutomationSwitchStepProps extends IEditAutomationStepProps {
	switchStep: Api.ISwitchAutomationStep | null;
}

interface IEditAutomationStepWithContextProps {
	caseStatement: Api.IAutomationStepCaseStatement;
	switchStep: Api.ISwitchAutomationStep;
	parentAutomationTemplateId?: string;
	onStepSaved: () => void;
}

const EditAutomationTemplateSegmentWithContext = (props: IEditAutomationStepWithContextProps) => {
	const { caseStatement, switchStep, parentAutomationTemplateId, onStepSaved } = props;
	const parentContext = React.useContext(EditAutomationTemplateStepsContext);

	const [unsavedStep, setUnsavedStep] = React.useState<Api.IAutomationStep>(null);
	const contextValue = React.useMemo(() => {
		return {
			automationTemplateId: caseStatement.automationTemplateId,
			impersonationContext: parentContext?.impersonationContext,
			unsavedStep,
			setUnsavedStep,
		};
	}, [caseStatement.automationTemplateId, parentContext?.impersonationContext, unsavedStep, setUnsavedStep]);

	return (
		<EditAutomationTemplateStepsContext.Provider value={contextValue}>
			<EditAutomationTemplateSegment
				caseStatement={caseStatement}
				key={caseStatement.automationTemplateId || caseStatement.id}
				switchStep={switchStep}
				onStepSaved={onStepSaved}
				parentAutomationTemplateId={parentAutomationTemplateId}
			/>
		</EditAutomationTemplateStepsContext.Provider>
	);
};

const EditAutomationSwitchStep = (props: IEditAutomationSwitchStepProps) => {
	const { switchStep, onStepChanged, automationTemplateId } = props;

	return (
		<div className={css(styleSheet.segments)}>
			{switchStep.cases?.map((caseStatement: Api.IAutomationStepCaseStatement, i: number) => {
				return (
					<div key={caseStatement.id}>
						{i > 0 && <EditAutomationTemplateItemSeparator className={css(styleSheet.segmentSeparator)} />}
						<EditAutomationTemplateSegmentWithContext
							caseStatement={caseStatement}
							switchStep={switchStep}
							onStepSaved={onStepChanged}
							parentAutomationTemplateId={automationTemplateId}
							key={caseStatement.id}
						/>
					</div>
				);
			})}
		</div>
	);
};

export const EditAutomationStep = (props: IEditAutomationStepProps) => {
	const {
		automationTemplateId,
		index,
		step,
		impersonationContext,
		stepIndicatorIconRef,
		scrollToAnchorRef,
		onStepChanged,
	} = props;

	// Use the custom hook for managing steps
	const {
		steps,
		canReorderSteps,
		executeActionForStep,
		automationTemplate,
		automationTemplateTrigger,
		setUnsavedStep,
	} = useAutomationEditorContext();

	const clearUnsavedStep = () => {
		setTimeout(() => {
			setUnsavedStep(null);
		}, 500);
	};

	const errorMessages = useErrorMessages();
	const { logApiError, logEvent } = useEventLogging('EditAutomationStep');
	const isLast = index === steps.length - 1;

	const automationTemplateIdStepOrderUpdateMutation = useAutomationTemplateIdStepOrderUpdateMutation();
	const updateStepMutation = useUpdateAutomationStepMutation();

	const reorderStep = async (action: AutomationStepAction.MoveDown | AutomationStepAction.MoveUp) => {
		if (!canReorderSteps) {
			throw Api.asApiError('Cannot reorder this steps until all steps have a selected type.');
		}
		const up = action === AutomationStepAction.MoveUp;
		const i = steps.indexOf(step);
		if ((up && i > 0) || (!up && i < steps.length - 1)) {
			const orderedSteps = [...steps];
			if (up) {
				orderedSteps.splice(i - 1, 0, step);
				orderedSteps.splice(i + 1, 1);
			} else {
				orderedSteps.splice(i + 2, 0, step);
				orderedSteps.splice(i, 1);
			}
			const orderedIds = orderedSteps.map(x => x.id);
			try {
				await automationTemplateIdStepOrderUpdateMutation.mutateAsync({
					id: automationTemplate.id,
					order: orderedIds,
					impersonationContext,
				});

				// TODO: Add invalidation here and remove all the junk below

				// Reorder the steps array to match the new order
				const updatedSteps: Api.IAutomationStep[] = [];
				orderedIds.forEach(x => {
					const s = automationTemplate.draft?.steps.find((y: Api.IAutomationStep) => y.id === x);
					if (s) {
						updatedSteps.push(s);
					}
				});
				// Update the steps array directly (no longer using MobX observable)
				if (automationTemplate.draft?.steps) {
					automationTemplate.draft.steps = updatedSteps;
				}
				if (!up && i === 0 && step.schedule?.criteria === Api.AutomationStepScheduleCriteria.Immediately) {
					const defaultSchedule = Api.VmUtils.Automations.steps.getDefaultScheduleForAutomationStep(
						step._type as Api.AutomationStepType,
						automationTemplateTrigger
					);
					if (!!defaultSchedule && !!defaultSchedule.criteria && defaultSchedule.criteria !== step.schedule.criteria) {
						// Get plain step data and update schedule via mutation
						const baseStepModel = step ? step : { ...step };
						const updatedStep = {
							...baseStepModel,
							schedule: {
								...baseStepModel.schedule,
								criteria: defaultSchedule.criteria,
							},
						};
						await updateStepMutation.mutateAsync({
							templateId: automationTemplateId,
							stepType: step._type as Api.AutomationStepType,
							stepId: step.id,
							step: updatedStep,
							impersonationContext,
						});
						// TODO: Update local state after successful API call
						// Update local state after successful API call
						// step.update?.(updatedStep);
					}
				}
			} catch (error) {
				logApiError('MenuItemClicked-Error', error);

				errorMessages.pushApiError(error);
			}
		}
	};

	const onMenuItemClicked = async (action: AutomationStepAction) => {
		if (action === AutomationStepAction.MoveDown || action === AutomationStepAction.MoveUp) {
			await reorderStep(action);
		} else {
			try {
				logEvent('MenuItemClicked', {
					action,
					automationTemplateId,
					stepId: step?.id,
				});
				await executeActionForStep(step, action);
			} catch (error) {
				logApiError('MenuItemClicked-Error', error);

				errorMessages.pushApiError(error);
			}
		}
		onStepChanged?.();
	};
	// Convert API _type (e.g., "SwitchAutomationStep") to enum format (e.g., "Switch")
	const stepType = step?._type ? apiModelTypeToAutomationStepType(step._type as Api.ApiModelType) : undefined;
	const switchStep = stepType === Api.AutomationStepType.Switch ? (step as Api.ISwitchAutomationStep) : null;

	// TODO: Verify this logic is still correct after updates
	// A step is a switch child if it's not the switch itself and its templateId differs from the parent
	// const isSwitchStepChild = !switchStep && !!step.automationTemplateId && step.meta.templateId !== template?.id;
	const isSwitchStepChild = false;

	const onRenderTitle = React.useMemo(() => {
		return isSwitchStepChild
			? () => {
					return null as JSX.Element;
				}
			: undefined;
	}, [isSwitchStepChild]);

	const onRenderLeftBumperContent = React.useCallback(() => {
		return (
			<div className={css(styleSheet.stepIndicator)}>
				<AutomationTemplateStepIndicatorIcon
					ref={stepIndicatorIconRef}
					step={step}
					styles={[
						styleSheet.stepIndicatorIcon,

						isSwitchStepChild ? styleSheet.stepIndicatorIconForSwitchChildStep : null,
					]}
					type={isSwitchStepChild ? 'small' : 'default'}
				>
					<span>{index + 1}</span>
				</AutomationTemplateStepIndicatorIcon>
				<div
					className={css(
						styleSheet.stepIndicatorIconHorizontalLine,
						isSwitchStepChild ? styleSheet.stepIndicatorIconHorizontalLineForSwitchChildStep : null
					)}
				/>
				{!isSwitchStepChild && (
					<>
						{!isLast && (
							<div
								className={css(
									styleSheet.stepIndicatorIconVerticalLine,
									styleSheet.stepIndicatorIconVerticalLineBottom
								)}
							/>
						)}
						<div className={css(styleSheet.stepIndicatorIconVerticalLine, styleSheet.stepIndicatorIconVerticalTop)} />
					</>
				)}
			</div>
		);
	}, [step, isSwitchStepChild, index, isLast, stepIndicatorIconRef]);

	if (!steps.length) return 'Loading...';

	const canEdit = true;

	return switchStep ? (
		<EditAutomationSwitchStep {...props} switchStep={switchStep} impersonationContext={impersonationContext} />
	) : (
		<EditAutomationTemplateItemBumpers
			onRenderLeftBumperContent={onRenderLeftBumperContent}
			scrollAnchorId={step?.id || step?.id}
			scrollToAnchorRef={scrollToAnchorRef}
		>
			<AutomationStepCard
				index={index}
				onMenuItemClicked={onMenuItemClicked}
				onRenderTitle={onRenderTitle}
				readonly={!canEdit}
				steps={steps}
				template={automationTemplate}
				onStepChanged={onStepChanged}
				impersonationContext={impersonationContext}
				clearUnsavedStep={clearUnsavedStep}
			/>
		</EditAutomationTemplateItemBumpers>
	);
};
